import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Checkbox, Chip, CircularProgress, TextField, Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

const useStyles = makeStyles((theme) => ({
  container: { marginBottom: theme.spacing(0.5) },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  autocomplete: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f5f5f5',
      minHeight: 40,
    },
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  optionName: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionParentPath: {
    fontSize: '11px',
    color: '#888',
    lineHeight: 1.1,
  },
  checkboxIcon: {
    marginRight: theme.spacing(1),
    color: '#9e9e9e',
  },
  checkboxCheckedIcon: {
    marginRight: theme.spacing(1),
    color: '#3f51b5',
  },
  chip: {
    backgroundColor: '#e8eaf6',
    color: '#3f51b5',
    fontWeight: 500,
    '& .MuiChip-deleteIcon': {
      color: '#7986cb',
      '&:hover': { color: '#3f51b5' },
    },
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '12px',
    marginTop: theme.spacing(0.5),
  },
}));

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Flattens the nested category tree into a depth-first list so a single
 * Autocomplete can show the full parent → child chain while searching.
 */
const flattenTree = (nodes, level = 0, parentPath = '') => {
  const out = [];
  (nodes || []).forEach((node) => {
    if (!node) return;
    const id = node.entity_id != null ? node.entity_id : node.id;
    if (id == null) return;
    const name = node?.name || node?.name_en || '';
    const path = parentPath ? `${parentPath} > ${name}` : name;
    out.push({
      id,
      name,
      level,
      path,
      parentPath,
    });
    if (Array.isArray(node.children) && node.children.length > 0) {
      out.push(...flattenTree(node.children, level + 1, path));
    }
  });
  return out;
};

const CategoryMultiSelect = ({
  label = 'Categories',
  placeholder = 'Search categories...',
  tree = [],
  values = [],
  onChange,
  loading = false,
  error,
}) => {
  const classes = useStyles();

  const flatOptions = useMemo(() => flattenTree(tree), [tree]);

  const mergedOptions = useMemo(() => {
    const byId = new Map();
    flatOptions.forEach((o) => byId.set(String(o.id), o));
    (values || []).forEach((v) => {
      if (v == null || v === '') return;
      const key = String(v);
      if (!byId.has(key)) {
        byId.set(key, {
          id: v, name: String(v), level: 0, path: String(v), parentPath: '',
        });
      }
    });
    return Array.from(byId.values());
  }, [flatOptions, values]);

  const selectedOptions = useMemo(() => {
    const selectedKeys = new Set((values || []).map((v) => String(v)));
    return mergedOptions.filter((o) => selectedKeys.has(String(o.id)));
  }, [mergedOptions, values]);

  const handleChange = (_, next) => onChange(next.map((o) => o.id));

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>{label}</Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={mergedOptions}
        value={selectedOptions}
        loading={loading}
        noOptionsText={loading ? 'Loading categories...' : 'No categories found'}
        onChange={handleChange}
        getOptionLabel={(option) => option.path || option.name || ''}
        getOptionSelected={(option, value) => String(option.id) === String(value.id)}
        className={classes.autocomplete}
        renderOption={(option, { selected }) => (
          <Box
            className={classes.optionRow}
            style={{ paddingLeft: (option.level || 0) * 16 }}
          >
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              size="small"
              className={selected ? classes.checkboxCheckedIcon : classes.checkboxIcon}
            />
            <span className={classes.optionName}>
              <span>{option.name}</span>
              {option.parentPath && (
                <span className={classes.optionParentPath}>{option.parentPath}</span>
              )}
            </span>
          </Box>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              key={String(option.id)}
              label={option.path || option.name}
              size="small"
              className={classes.chip}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && (
        <Typography className={classes.errorText}>
          Failed to load categories. Showing previously saved values only.
        </Typography>
      )}
    </Box>
  );
};

export default CategoryMultiSelect;
